import { useInfiniteQuery, useMutation, useQuery } from 'react-query';
import { useDispatch } from 'react-redux';
import camelcaseKeys from 'camelcase-keys';

import { insuredApi, recordApi } from '../api';
import { createErrorToast, createSuccessToast, createToast } from '../redux/modules/app';
import { insuredSearch } from '../redux/modules/insureds';
import { itemSearch } from '../redux/modules/items';
import { reportsResetChecked } from '../redux/modules/reports';
import { queryClient } from '../utils/queryClientUtil';

import insuredKeys from './models/insured/keys';
import recordKeys from './models/record/keys';
import { camelCaseApiResponse, deleteInfiniteListsItem, getPageData, modifyInfiniteListsItem } from './util';

/* QUERIES
========================================================= */
export const useInsuredData = (insuredId, options = {}) => useQuery(
  insuredKeys.insured(insuredId),
  () => camelCaseApiResponse(() => insuredApi.getInsuredById(insuredId)),
  options
);

export const useInsuredOverallStatusData = (insuredId, options = {}) => useQuery(
  insuredKeys.insuredOverallStatus(insuredId),
  () => camelCaseApiResponse(() => insuredApi.getInsuredOverallStatus(insuredId)),
  options
);

export const useInsuredAllRecordsData = ({
  insuredId,
  pageSize = 25,
  options = {}
}) => useInfiniteQuery(
  insuredKeys.insuredAllRecords(insuredId),
  ({ pageParam = 1 }) => camelCaseApiResponse(() => insuredApi.getInsuredRecords(
    insuredId,
    { page: pageParam, pageSize }
  )),
  {
    ...options,
    getNextPageParam: getPageData
  }
);

export const useInsuredActiveRecordsData = ({
  insuredId,
  pageSize = 25,
  options = {}
}) => useInfiniteQuery(
  insuredKeys.insuredActiveRecords(insuredId),
  ({ pageParam = 1 }) => camelCaseApiResponse(() => insuredApi.getInsuredRecords(
    insuredId,
    { page: pageParam, pageSize },
    { active: true }
  )),
  {
    ...options,
    getNextPageParam: getPageData
  }
);

export const useInsuredArchivedRecordsData = ({
  insuredId,
  pageSize = 10,
  options = {}
}) => useInfiniteQuery(
  insuredKeys.insuredArchivedRecords(insuredId),
  ({ pageParam = 1 }) => camelCaseApiResponse(() => insuredApi.getInsuredRecords(
    insuredId,
    { page: pageParam, pageSize },
    { archived: true }
  )),
  {
    ...options,
    getNextPageParam: getPageData
  }
);

export const useInsuredAllRecordsDataWithTrashed = ({
  insuredId,
  pageSize = 100,
  options = {}
}) => useInfiniteQuery(
  insuredKeys.insuredAllRecordsWithTrashed(insuredId),
  ({ pageParam = 1 }) => camelCaseApiResponse(() => insuredApi.getInsuredRecords(
    insuredId,
    { page: pageParam, pageSize },
    { all: true }
  )),
  {
    ...options,
    getNextPageParam: getPageData
  }
);

export const useAllInsuredRecords = ({
  insuredId,
  options = {}
}) => useQuery(
  insuredKeys.insuredEveryRecord(insuredId),
  () => camelCaseApiResponse(() => insuredApi.getAllInsuredRecords(insuredId)),
  options
);

export const useInsuredEmails = ({
  insuredId,
  recordId,
  order = 'desc',
  pageSize = 50,
  options = {}
}) => useInfiniteQuery(
  insuredKeys.insuredEmails(insuredId, recordId, order),
  ({ pageParam = 1 }) => camelCaseApiResponse(() => insuredApi.getInsuredEmails(
    insuredId,
    recordId,
    order,
    { page: pageParam, pageSize }
  )),
  {
    ...options,
    getNextPageParam: getPageData
  }
);

export const useInsuredAutomatedEmails = ({
  insuredId,
  recordId,
  order = 'desc',
  pageSize = 50,
  options = {}
}) => useInfiniteQuery(
  insuredKeys.insuredAutomatedEmails(insuredId, recordId, order),
  ({ pageParam = 1 }) => camelCaseApiResponse(() => insuredApi.getInsuredAutomatedEmails(
    insuredId,
    recordId,
    order,
    { page: pageParam, pageSize }
  )),
  {
    ...options,
    getNextPageParam: getPageData
  }
);

/* MUTATIONS
========================================================= */
export const useRefreshInsured = ({
  insuredId,
  recordId,
  options
}) => {
  const dispatch = useDispatch();
  const activeRecordsKey = insuredKeys.insuredActiveRecords(insuredId);
  const overallStatus = insuredKeys.insuredOverallStatus(insuredId);
  const recordKey = recordKeys.record(recordId);
  const policyRequirementsKey = recordKeys.recordPolicyRequirements(recordId);
  const documentRequirementsKey = recordKeys.recordDocumentRequirements(recordId);
  const itemRequirementsKey = recordKeys.recordItemRequirements(recordId);
  const policiesKey = recordKeys.recordPolicies(recordId);
  const documentsKey = recordKeys.recordDocuments(recordId);

  return useMutation(
    () => camelCaseApiResponse(() => insuredApi.refreshInsured(insuredId)),
    {
      onMutate: async onSuccessFunc => {
        await queryClient.cancelQueries(activeRecordsKey);
        await queryClient.cancelQueries(overallStatus);
        await queryClient.cancelQueries(recordKey);
        await queryClient.cancelQueries(policyRequirementsKey);
        await queryClient.cancelQueries(documentRequirementsKey);
        await queryClient.cancelQueries(itemRequirementsKey);
        await queryClient.cancelQueries(policiesKey);
        await queryClient.cancelQueries(documentsKey);

        dispatch(createToast({ title: 'Refreshing Insured...', duration: 60000 }));

        return { };
      },
      // eslint-disable-next-line no-unused-vars
      onError: (_error, variables, _context) => {
        dispatch(createErrorToast({
          title: 'Oops! Something went wrong...',
          description: 'Your insured was not refreshed successfully.'
        }));

        // variables is only an onSuccessFunction
        variables();
      },
      onSuccess: async (_data, variables) => {
        dispatch(createToast({ title: 'Fetching data...', duration: 60000 }));

        dispatch(itemSearch.resetFilters());

        Promise.all([
          queryClient.invalidateQueries(activeRecordsKey),
          queryClient.invalidateQueries(overallStatus),
          queryClient.invalidateQueries(recordKey),
          queryClient.invalidateQueries(policyRequirementsKey),
          queryClient.invalidateQueries(documentRequirementsKey),
          queryClient.invalidateQueries(itemRequirementsKey),
          queryClient.invalidateQueries(policiesKey),
          queryClient.invalidateQueries(documentsKey)
        ]);

        dispatch(createSuccessToast({ title: 'Insured Refreshed!' }));

        // variables is only an onSuccessFunction
        variables();
      },
      ...options
    }
  );
};

export const useUpdateInsuredData = (insuredId, options) => {
  const dispatch = useDispatch();
  const insuredKey = insuredKeys.insured(insuredId);

  return useMutation(
    data => camelCaseApiResponse(() => insuredApi.updateInsured(insuredId, data)),
    {
      onMutate: async updateData => {
        await queryClient.cancelQueries(insuredKey);

        const previousInsured = queryClient.getQueryData(insuredKey);

        queryClient.setQueryData(insuredKey, { ...previousInsured, ...camelcaseKeys(updateData) });

        dispatch(createSuccessToast({
          title: 'Insured Updated!',
          status: 'success'
        }));

        return { previousInsured };
      },
      onError: (_error, _insured, context) => {
        dispatch(createErrorToast({
          title: 'Oops! Something went wrong...',
          description: 'Your insured was not updated successfully.'
        }));

        queryClient.setQueryData(insuredKey, context.previousInsured);
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries(insuredKey);
        // The Remove Agent preview is derived from the insured's contact email, so any insured
        // update can change whether it reports a contact replacement. Previews are keyed per
        // record AND per agent, and one insured spans many records, so match on the shared
        // segment rather than trying to enumerate them.
        const isAgentRemovalPreview = q => q.queryKey.includes(recordKeys.agentRemovalPreviewSegment);
        await queryClient.invalidateQueries({ predicate: isAgentRemovalPreview });
      },
      ...options
    }
  );
};

export const useArchiveInsuredInsuranceRequirementData = (insuredId, recordId, options = {}) => {
  const dispatch = useDispatch();
  const activeListKey = insuredKeys.insuredActiveRecords(insuredId);
  const insuredOverallStatusKey = insuredKeys.insuredOverallStatus(insuredId);

  return useMutation(
    () => camelCaseApiResponse(() => recordApi.archiveRecord(recordId)),
    {
      onMutate: async () => {
        await queryClient.cancelQueries(insuredKeys.insuredActiveRecords(insuredId));
        await queryClient.cancelQueries(insuredKeys.insuredAllRecords(insuredId));
        await queryClient.cancelQueries(insuredKeys.insuredArchivedRecords(insuredId));
        await queryClient.cancelQueries(insuredKeys.insuredAllRecordsWithTrashed(insuredId));

        const previousActiveList = queryClient.getQueryData(activeListKey);

        deleteInfiniteListsItem(
          [activeListKey],
          'id',
          recordId
        );

        dispatch(createSuccessToast({
          title: 'Record Archived!',
          status: 'success'
        }));

        return { previousActiveList };
      },
      onError: (_error, _insured, context) => {
        dispatch(createErrorToast({
          title: 'Oops! Something went wrong...',
          description: 'Your record was not archived successfully.'
        }));

        queryClient.setQueryData(activeListKey, context.previousActiveList);
      },
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries(insuredKeys.insuredActiveRecords(insuredId)),
          queryClient.invalidateQueries(insuredKeys.insuredArchivedRecords(insuredId)),
          queryClient.invalidateQueries(insuredKeys.insuredAllRecordsWithTrashed(insuredId)),
          queryClient.invalidateQueries(insuredOverallStatusKey)
        ]);
      },
      ...options
    }
  );
};

export const useReactivateInsuredInsuranceRequirementData = (insuredId, recordId, options = {}) => {
  const dispatch = useDispatch();
  const archivedListKey = insuredKeys.insuredArchivedRecords(insuredId);
  const insuredOverallStatusKey = insuredKeys.insuredOverallStatus(insuredId);

  return useMutation(
    () => camelCaseApiResponse(() => recordApi.reactivateRecord(recordId)),
    {
      onMutate: async () => {
        await queryClient.cancelQueries(insuredKeys.insuredAllRecords(insuredId));
        await queryClient.cancelQueries(insuredKeys.insuredActiveRecords(insuredId));
        await queryClient.cancelQueries(insuredKeys.insuredArchivedRecords(insuredId));
        await queryClient.cancelQueries(insuredKeys.insuredAllRecordsWithTrashed(insuredId));
        const previousArchivedList = queryClient.getQueryData(archivedListKey);

        deleteInfiniteListsItem(
          [archivedListKey],
          'id',
          recordId
        );

        dispatch(createToast({
          title: 'Reactivating record.',
          description: 'Grabbing the record details...',
          duration: 10000
        }));

        // Fallback if mutation fails
        return { previousArchivedList };
      },
      onError: (_error, _insured, context) => {
        dispatch(createErrorToast({
          title: 'Oops! Something went wrong...',
          description: 'Your record was not reactivated successfully.'
        }));

        queryClient.setQueryData(archivedListKey, context.previousArchivedList);
      },
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries(insuredKeys.insuredAllRecords(insuredId)),
          queryClient.invalidateQueries(insuredKeys.insuredArchivedRecords(insuredId)),
          queryClient.invalidateQueries(insuredKeys.insuredActiveRecords(insuredId)),
          queryClient.invalidateQueries(insuredKeys.insuredAllRecordsWithTrashed(insuredId)),
          queryClient.invalidateQueries(insuredOverallStatusKey)
        ]);

        dispatch(createSuccessToast({ title: 'Record Reactivated' }));
      },
      ...options
    }
  );
};

export const useDeleteInsuranceRequirementData = (insuredId, recordId, options = {}) => {
  const dispatch = useDispatch();
  const activeListKey = insuredKeys.insuredActiveRecords(insuredId);
  const archivedListKey = insuredKeys.insuredArchivedRecords(insuredId);

  return useMutation(
    () => camelCaseApiResponse(() => recordApi.deleteRecord(recordId)),
    {
      onMutate: async () => {
        await queryClient.cancelQueries(insuredKeys.insuredAllRecords(insuredId));
        await queryClient.cancelQueries(insuredKeys.insuredActiveRecords(insuredId));
        await queryClient.cancelQueries(insuredKeys.insuredArchivedRecords(insuredId));
        await queryClient.cancelQueries(insuredKeys.insuredAllRecordsWithTrashed(insuredId));
        await queryClient.cancelQueries(insuredKeys.insuredAllRecordsWithTrashed(insuredId));
        const previousActiveList = queryClient.getQueryData(activeListKey);
        const previousArchivedList = queryClient.getQueryData(archivedListKey);

        dispatch(createToast({
          title: 'Deleting Record...',
          duration: 10000
        }));

        // Fallback if mutation fails
        return { previousArchivedList, previousActiveList };
      },
      onError: (_error, _insured, context) => {
        dispatch(createErrorToast({
          title: 'Oops! Something went wrong...',
          description: 'Your record was not deleted successfully.'
        }));

        queryClient.setQueryData(archivedListKey, context.previousArchivedList);
        queryClient.setQueryData(activeListKey, context.previousActiveList);
      },
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries(insuredKeys.insuredAllRecords(insuredId)),
          queryClient.invalidateQueries(insuredKeys.insuredArchivedRecords(insuredId)),
          queryClient.invalidateQueries(insuredKeys.insuredActiveRecords(insuredId)),
          queryClient.invalidateQueries(insuredKeys.insuredAllRecordsWithTrashed(insuredId)),
          queryClient.invalidateQueries(insuredKeys.insuredOverallStatus(insuredId))
        ]);

        dispatch(createSuccessToast({ title: 'Record Deleted!' }));
      },
      ...options
    }
  );
};

export const useUpdateInsuranceRequirementListData = (insuredId, recordId, options) => {
  const dispatch = useDispatch();
  const allListKey = insuredKeys.insuredAllRecords(insuredId);
  const activeListKey = insuredKeys.insuredActiveRecords(insuredId);
  const archivedListKey = insuredKeys.insuredArchivedRecords(insuredId);
  const allListWithTrashedKey = insuredKeys.insuredAllRecordsWithTrashed(insuredId);

  return useMutation(
    data => camelCaseApiResponse(() => recordApi.updateInsuranceRequirement(recordId, data)),
    {
      onMutate: async updateData => {
        await queryClient.cancelQueries(allListKey);
        await queryClient.cancelQueries(activeListKey);
        await queryClient.cancelQueries(archivedListKey);
        await queryClient.cancelQueries(allListWithTrashedKey);
        const previousActiveList = queryClient.getQueryData(activeListKey);
        const previousArchivedList = queryClient.getQueryData(archivedListKey);
        const previousAllList = queryClient.getQueryData(allListKey);
        const previousAllWithTrashedList = queryClient.getQueryData(allListWithTrashedKey);

        modifyInfiniteListsItem(
          [allListKey, activeListKey, archivedListKey, allListWithTrashedKey],
          'id',
          recordId,
          record => ({ ...record, ...camelcaseKeys(updateData) })
        );

        dispatch(createSuccessToast({ title: 'Record Updated!' }));

        return {
          previousActiveList,
          previousArchivedList,
          previousAllList,
          previousAllWithTrashedList
        };
      },
      onError: (_error, _insured, context) => {
        dispatch(createErrorToast({
          title: 'Oops! Something went wrong...',
          description: 'Your record was not updated successfully.'
        }));

        queryClient.setQueryData(allListKey, context.previousAllList);
        queryClient.setQueryData(activeListKey, context.previousActiveList);
        queryClient.setQueryData(archivedListKey, context.previousArchivedList);
        queryClient.setQueryData(allListWithTrashedKey, context.previousAllWithTrashedList);
      },
      onSuccess: () => {
        queryClient.invalidateQueries(allListKey);
        queryClient.invalidateQueries(activeListKey);
        queryClient.invalidateQueries(archivedListKey);
        queryClient.invalidateQueries(allListWithTrashedKey);
        // Record information feeds the Remove Agent preview, so drop this record's cached
        // previews for every agent.
        queryClient.invalidateQueries(recordKeys.recordAgentRemovalPreviews(recordId));
      },
      ...options
    }
  );
};

export const useMergeInsureds = () => {
  const dispatch = useDispatch();

  return useMutation(
    data => {
      const { insuredId, insuredsToMerge } = data;

      return camelCaseApiResponse(() => insuredApi.mergeInsureds(insuredId, insuredsToMerge));
    },
    {
      onMutate: async data => {
        const { insuredId } = data;

        const allListKey = insuredKeys.insuredAllRecords(insuredId);
        const activeListKey = insuredKeys.insuredActiveRecords(insuredId);
        const archivedListKey = insuredKeys.insuredArchivedRecords(insuredId);
        const allListWithTrashedKey = insuredKeys.insuredAllRecordsWithTrashed(insuredId);

        await queryClient.cancelQueries(allListKey);
        await queryClient.cancelQueries(activeListKey);
        await queryClient.cancelQueries(archivedListKey);
        await queryClient.cancelQueries(allListWithTrashedKey);

        dispatch(createToast({ title: 'Merging Insureds...', duration: 60000 }));
      },
      onError: (_error, _insured, context) => {
        dispatch(insuredSearch.getPage());
        dispatch(reportsResetChecked());

        dispatch(createErrorToast({
          title: 'Oops! Something went wrong with your merge...',
          description: 'Please allow 2 minutes for PINS to reset your data correctly.',
          duration: 60000
        }));
      },
      onSuccess: (data, variables, context) => {
        const { insuredId } = variables;

        const allListKey = insuredKeys.insuredAllRecords(insuredId);
        const activeListKey = insuredKeys.insuredActiveRecords(insuredId);
        const archivedListKey = insuredKeys.insuredArchivedRecords(insuredId);
        const allListWithTrashedKey = insuredKeys.insuredAllRecordsWithTrashed(insuredId);

        queryClient.invalidateQueries(allListKey);
        queryClient.invalidateQueries(activeListKey);
        queryClient.invalidateQueries(archivedListKey);
        queryClient.invalidateQueries(allListWithTrashedKey);

        dispatch(reportsResetChecked());

        dispatch(insuredSearch.getPage());

        dispatch(createSuccessToast({
          title: 'Merge Successful!',
          description: 'It may take a minute to see your changes'
        }));
      }
    }
  );
};

